// src/pages/SocialMeetupDetailPage.jsx
import { useTranslation } from "../i18n";
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import '../components/Social/SocialMeetupsPanel.css';
import './SocialMeetupDetailPage.css';
import API_BASE_URL from '../config/api';

// Mismo pin personalizado (amarillo, con "TZ") que usa la app movil en el
// detalle de eventos - para que ambas plataformas se vean iguales, en vez
// del marcador generico del iframe de OpenStreetMap.
const meetingIcon = L.divIcon({
  className: 'tzone-meeting-marker',
  html: `
    <div style="
      width: 34px;
      height: 34px;
      border-radius: 50% 50% 50% 0;
      background: #f9c93c;
      border: 3px solid #111827;
      transform: rotate(-45deg);
      box-shadow: 0 8px 18px rgba(0,0,0,.35);
      display: flex;
      align-items: center;
      justify-content: center;
    ">
      <span style="
        transform: rotate(45deg);
        color: #111827;
        font-weight: 900;
        font-size: 12px;
        letter-spacing: 0;
      ">TZ</span>
    </div>
  `,
  iconSize: [34, 34],
  iconAnchor: [17, 34],
});

const isAbsUrl = (v) => typeof v === 'string' && (v.startsWith('http://') || v.startsWith('https://'));

function resolveAvatarUrl(photoUrl) {
  const u = typeof photoUrl === 'string' ? photoUrl.trim() : '';
  if (!u) return '';
  if (isAbsUrl(u)) return u;
  if (u.startsWith('/')) return `${API_BASE_URL}${u}`;
  return `${API_BASE_URL}/${u}`;
}

// Pin para marcar a un participante en el mapa de ubicacion en vivo
// (distinto al de "punto de encuentro" para no confundirlos). Usa su
// foto de perfil cuando existe, para reconocer a cada uno de un vistazo.
function buildParticipantIcon(name, photoUrl) {
  const src = resolveAvatarUrl(photoUrl);
  const initial = (name || '?').trim().charAt(0).toUpperCase();
  const inner = src
    ? `<img src="${src}" style="width:100%;height:100%;object-fit:cover;border-radius:50%;" />`
    : `<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;background:#193255;color:#fff;font-weight:800;font-size:12px;">${initial}</div>`;

  return L.divIcon({
    className: 'tzone-participant-marker',
    html: `
      <div style="
        width: 30px;
        height: 30px;
        border-radius: 50%;
        overflow: hidden;
        border: 3px solid #fff;
        box-shadow: 0 6px 14px rgba(0,0,0,.4);
      ">${inner}</div>
    `,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
  });
}

function normalizeGpxPoints(points) {
  if (!Array.isArray(points)) return [];
  return points
    .map((p) => {
      if (!Array.isArray(p) || p.length < 2) return null;
      const lat = Number(p[0]);
      const lon = Number(p[1]);
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
      if (Math.abs(lat) > 90 || Math.abs(lon) > 180) return null;
      return [lat, lon];
    })
    .filter(Boolean);
}

const SocialMeetupDetailPage = () => {
  const { t } = useTranslation();
  const { eventId } = useParams();
  const navigate = useNavigate();

  const [status, setStatus] = useState('loading'); // loading | success | error
  const [message, setMessage] = useState('');
  const [event, setEvent] = useState(null);

  const [chatMessages, setChatMessages] = useState([]);
  const [chatText, setChatText] = useState('');
  const [chatSending, setChatSending] = useState(false);

  // Ubicacion en vivo: compartir (participante) y ver (organizador).
  // Siempre en primer plano (mientras la pagina esta abierta) y siempre
  // opt-in - nunca se activa sola.
  const [sharingLocation, setSharingLocation] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [liveLocations, setLiveLocations] = useState([]);

  // Confirmar/rechazar asistencia: aplica cuando te auto-sumaron por ser
  // miembro del grupo (myStatus === 'invited') y todavia no respondiste.
  const [respondingParticipation, setRespondingParticipation] = useState(false);
  const [reinvitingUserId, setReinvitingUserId] = useState(null);

  // Fotos de perfil que dieron error al cargar (link roto, cuenta vieja
  // sin la carpeta correcta, etc.) - se cae a las iniciales en vez de
  // dejar el icono de imagen rota.
  const [brokenAvatarKeys, setBrokenAvatarKeys] = useState(() => new Set());
  const markAvatarBroken = (key) => {
    setBrokenAvatarKeys((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  };

  const userRole = localStorage.getItem('userRole') || '';
  const isLoggedIn = !!userRole;

  const authHeaders = () => {
    const token = localStorage.getItem('token') || '';
    const userId = localStorage.getItem('userId') || '';
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
    if (userId) headers['x-user-id'] = userId;
    return headers;
  };

  useEffect(() => {
    if (!eventId) {
      setStatus('error');
      setMessage('El identificador del encuentro no es válido.');
      return;
    }

    const fetchEvent = async () => {
      try {
        setStatus('loading');
        setMessage('Cargando detalles del encuentro...');

        // Endpoint PUBLICO (funciona sin login desde el link de invitacion),
        // pero si hay sesion mandamos el token igual: asi el backend puede
        // decirnos si ya estamos inscritos y mostrar el chat/participantes.
        const resp = await fetch(
          `${API_BASE_URL}/api/social/events/public/${eventId}`,
          { method: 'GET', headers: authHeaders() }
        );

        const data = await resp.json().catch(() => ({}));

        if (!resp.ok) {
          throw new Error(data.message || 'No fue posible cargar el encuentro.');
        }
        if (!data.event) {
          throw new Error('No se encontraron detalles del encuentro.');
        }

        setEvent(data.event);
        setStatus('success');
        setMessage('');
      } catch (err) {
        console.error('[SocialMeetupDetail] error', err);
        setStatus('error');
        setMessage(err.message || 'Ocurrió un problema al cargar los detalles del encuentro.');
      }
    };

    fetchEvent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const canUseChat = Boolean(event?.isJoined);
  const isOrganizer = Boolean(event?.isOrganizer);

  useEffect(() => {
    if (!eventId || !canUseChat) return;
    let cancelled = false;

    const loadMessages = async () => {
      try {
        const resp = await fetch(`${API_BASE_URL}/api/social/events/${eventId}/messages`, {
          headers: authHeaders(),
        });
        const data = await resp.json().catch(() => []);
        if (!cancelled && resp.ok && Array.isArray(data)) setChatMessages(data);
      } catch {
        // silencioso
      }
    };

    loadMessages();
    const interval = setInterval(loadMessages, 15000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, canUseChat]);

  // Compartir mi ubicacion (participante): solo mientras `sharingLocation`
  // este activo y la pagina siga abierta - foreground-only por diseño, no
  // usamos watchPosition en segundo plano ni permiso "Always".
  useEffect(() => {
    if (!sharingLocation || !eventId) return;
    let cancelled = false;

    const sendPosition = () => {
      if (!navigator.geolocation) {
        setLocationError('Tu navegador no soporta geolocalización.');
        setSharingLocation(false);
        return;
      }
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          if (cancelled) return;
          try {
            const resp = await fetch(`${API_BASE_URL}/api/social/events/${eventId}/live-location`, {
              method: 'POST',
              headers: authHeaders(),
              body: JSON.stringify({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
            });
            if (!resp.ok) {
              const data = await resp.json().catch(() => ({}));
              throw new Error(data.message || 'No se pudo compartir tu ubicación.');
            }
            setLocationError('');
          } catch (err) {
            setLocationError(err.message || 'No se pudo compartir tu ubicación.');
          }
        },
        (err) => {
          if (cancelled) return;
          setLocationError('No pudimos acceder a tu ubicación: ' + (err.message || 'permiso denegado.'));
          setSharingLocation(false);
        },
        { enableHighAccuracy: false, timeout: 10000 }
      );
    };

    sendPosition();
    const interval = setInterval(sendPosition, 20000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sharingLocation, eventId]);

  // Ver ubicacion en vivo del grupo (solo organizador).
  useEffect(() => {
    if (!isOrganizer || !eventId) return;
    let cancelled = false;

    const loadLocations = async () => {
      try {
        const resp = await fetch(`${API_BASE_URL}/api/social/events/${eventId}/live-locations`, {
          headers: authHeaders(),
        });
        const data = await resp.json().catch(() => []);
        if (!cancelled && resp.ok && Array.isArray(data)) setLiveLocations(data);
      } catch {
        // silencioso
      }
    };

    loadLocations();
    const interval = setInterval(loadLocations, 20000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOrganizer, eventId]);

  const handleSendChat = async () => {
    const text = chatText.trim();
    if (!text || !eventId) return;
    try {
      setChatSending(true);
      const resp = await fetch(`${API_BASE_URL}/api/social/events/${eventId}/messages`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ text }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.message || 'No se pudo enviar el mensaje.');
      setChatMessages((prev) => [...prev, data]);
      setChatText('');
    } catch (err) {
      alert(err.message || 'No se pudo enviar el mensaje.');
    } finally {
      setChatSending(false);
    }
  };

  const respondParticipation = async (action) => {
    if (!eventId) return;
    try {
      setRespondingParticipation(true);
      const resp = await fetch(
        `${API_BASE_URL}/api/social/events/${eventId}/participation/${action}`,
        { method: 'POST', headers: authHeaders() }
      );
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.message || 'No se pudo responder.');

      if (action === 'decline') {
        navigate('/social/meetups/board');
        return;
      }

      // Refresca el detalle para reflejar el nuevo estado (confirmed/waitlisted)
      const refreshed = await fetch(`${API_BASE_URL}/api/social/events/public/${eventId}`, {
        headers: authHeaders(),
      });
      const refreshedData = await refreshed.json().catch(() => null);
      if (refreshed.ok && refreshedData?.event) {
        setEvent(refreshedData.event);
        alert(
          data.waitlisted
            ? 'Este encuentro ya está lleno - quedaste en lista de espera.'
            : '¡Listo! Confirmaste tu asistencia.'
        );
      }
    } catch (err) {
      alert(err.message || 'No se pudo responder.');
    } finally {
      setRespondingParticipation(false);
    }
  };

  // Cancelar cuando YA estabas confirmado o en lista de espera (distinto
  // de "decline" en participation, que es solo para la invitacion inicial
  // sin responder). Libera el cupo de verdad: el backend promueve al
  // siguiente en la lista de espera si corresponde.
  const handleLeaveEvent = async () => {
    if (!eventId) return;
    if (!window.confirm('¿Seguro que ya no puedes ir a este encuentro? Se liberará tu cupo.')) return;
    try {
      setRespondingParticipation(true);
      const resp = await fetch(`${API_BASE_URL}/api/social/events/${eventId}/leave`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.message || 'No se pudo cancelar tu asistencia.');
      navigate('/social/meetups/board');
    } catch (err) {
      alert(err.message || 'No se pudo cancelar tu asistencia.');
    } finally {
      setRespondingParticipation(false);
    }
  };

  // El organizador puede llevar a cualquier invitado a cualquier estado -
  // no solo "reinvitar" a quien cancelo. Un solo endpoint para las 4
  // acciones, con su propio aviso al atleta en cada caso.
  const statusActionLabels = {
    invited: 'Pedir que confirme',
    confirmed: 'Marcar Confirmado',
    waitlisted: 'Pasar a lista de espera',
    cancelled: 'Sacar del encuentro',
  };

  const handleSetParticipantStatus = async (participant, newStatus) => {
    if (!eventId || !participant?.userId || newStatus === participant.status) return;
    if (
      newStatus === 'cancelled' &&
      !window.confirm(`¿Sacar a ${participant.name} de este encuentro? Se le avisará.`)
    ) {
      return;
    }
    try {
      setReinvitingUserId(participant.userId);
      const resp = await fetch(
        `${API_BASE_URL}/api/social/events/${eventId}/participants/${participant.userId}/status`,
        {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({ status: newStatus }),
        }
      );
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.message || 'No se pudo actualizar el estado.');

      // Refresca desde el servidor - si hubo promocion desde lista de
      // espera, otro participante tambien cambio de estado.
      const refreshed = await fetch(`${API_BASE_URL}/api/social/events/public/${eventId}`, {
        headers: authHeaders(),
      });
      const refreshedData = await refreshed.json().catch(() => null);
      if (refreshed.ok && refreshedData?.event) setEvent(refreshedData.event);
    } catch (err) {
      alert(err.message || 'No se pudo actualizar el estado.');
    } finally {
      setReinvitingUserId(null);
    }
  };

  const goBack = () => {
    if (isLoggedIn) {
      navigate('/social/meetups/board');
    } else {
      navigate('/login');
    }
  };

  const formatDateTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString('es-CL', { dateStyle: 'full', timeStyle: 'short' });
  };

  // Valida que el evento tenga coordenadas utilizables para el mapa
  const eventLat = event ? Number(event.lat) : NaN;
  const eventLon = event ? Number(event.lon) : NaN;
  const hasCoords = Number.isFinite(eventLat) && Number.isFinite(eventLon);
  const routePoints = event ? normalizeGpxPoints(event.routeGpxPoints) : [];
  const routeMapCenter = routePoints.length > 0 ? routePoints[0] : [eventLat, eventLon];

  return (
    <div className="social-meetups-root">
      <div className="social-meetups-inner">
        <div className="social-meetups-header">
          <div>
            <h1>{event?.title || 'Detalle del encuentro'}</h1>
            <p>Revisa la fecha, el lugar, la ruta y conversa con el grupo antes de salir.</p>
          </div>
          <button type="button" className="social-meetups-btn" onClick={goBack}>
            ← Volver al tablero
          </button>
        </div>

        {status !== 'success' && (
          <div className="social-meetups-message" style={{ color: status === 'error' ? '#b91c1c' : '#193255' }}>
            {status === 'loading' ? message || 'Cargando detalles del encuentro...' : message}
          </div>
        )}

        {status === 'success' && event?.myStatus === 'invited' && (
          <div className="social-meetups-confirm-banner">
            <span>
              Tu grupo te sumó a este encuentro. ¿Confirmas tu asistencia?
            </span>
            <div className="social-meetups-confirm-banner-actions">
              <button
                type="button"
                className="social-cta-primary"
                disabled={respondingParticipation}
                onClick={() => respondParticipation('confirm')}
              >
                Confirmar
              </button>
              <button
                type="button"
                className="social-cta-secondary"
                disabled={respondingParticipation}
                onClick={() => respondParticipation('decline')}
              >
                No voy
              </button>
            </div>
          </div>
        )}

        {status === 'success' &&
          !event?.isOrganizer &&
          (event?.myStatus === 'confirmed' || event?.myStatus === 'waitlisted') && (
            <div className="social-meetups-confirm-banner">
              <span>
                {event.myStatus === 'waitlisted'
                  ? 'Estás en lista de espera para este encuentro.'
                  : 'Ya estás confirmado en este encuentro.'}
              </span>
              <div className="social-meetups-confirm-banner-actions">
                <button
                  type="button"
                  className="social-cta-secondary"
                  disabled={respondingParticipation}
                  onClick={handleLeaveEvent}
                >
                  Ya no puedo ir
                </button>
              </div>
            </div>
          )}

        {status === 'success' && event && (
          <div className="social-meetups-detail-grid">
            <section className="social-meetups-section">
              <h2>Información del encuentro</h2>

              {event.groupName && (
                <p className="social-meetups-detail-row">
                  <span className="social-meetups-detail-label">{t('groupsPanel.groups')}</span>
                  {event.groupName}
                </p>
              )}

              {event.dateTimeText && (
                <p className="social-meetups-detail-row">
                  <span className="social-meetups-detail-label">Fecha y hora</span>
                  {formatDateTime(event.dateTimeText)}
                </p>
              )}

              {(event.locationName || event.city || event.commune || event.country) && (
                <p className="social-meetups-detail-row">
                  <span className="social-meetups-detail-label">
                    {t('social.meetupsPanel.location.title')}
                  </span>
                  {[event.locationName, event.commune, event.city, event.country]
                    .filter(Boolean)
                    .join(', ')}
                </p>
              )}

              {typeof event.maxParticipants === 'number' && event.maxParticipants > 0 && (
                <p className="social-meetups-detail-row">
                  <span className="social-meetups-detail-label">Cupo máximo</span>
                  {event.maxParticipants} participantes
                </p>
              )}

              {event.contactPhone && (
                <p className="social-meetups-detail-row">
                  <span className="social-meetups-detail-label">Teléfono de emergencia</span>
                  {event.contactPhone}
                </p>
              )}

              {(event.routeUrl || event.routeGpxUrl) && (
                <p className="social-meetups-detail-row">
                  <span className="social-meetups-detail-label">Ruta del encuentro</span>
                  <span style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                    {event.routeUrl && (
                      <a href={event.routeUrl} target="_blank" rel="noopener noreferrer">
                        Ver ruta (Strava / mapa)
                      </a>
                    )}
                    {event.routeGpxUrl && (
                      <a href={`${API_BASE_URL}${event.routeGpxUrl}`} target="_blank" rel="noopener noreferrer">
                        Descargar ruta (.gpx)
                      </a>
                    )}
                  </span>
                </p>
              )}

              <p className="social-meetups-detail-row">
                <span className="social-meetups-detail-label">Calendario</span>
                <a href={`${API_BASE_URL}/api/social/events/${eventId}/calendar.ics`}>
                  📅 Agregar a mi calendario
                </a>
              </p>

              {event.description && (
                <p className="social-meetups-detail-description">{event.description}</p>
              )}
            </section>

            {canUseChat && Array.isArray(event.participants) && event.participants.length > 0 && (
              <section className="social-meetups-section">
                <h2>Participantes ({event.participants.length})</h2>
                {event.isOrganizer && (
                  <p className="social-meetups-participants-summary">
                    {event.participants.filter((p) => p.status === 'confirmed').length} confirmados
                    {' · '}
                    {event.participants.filter((p) => p.status === 'invited').length} pendientes de confirmar
                    {' · '}
                    {event.participants.filter((p) => p.status === 'waitlisted').length} en lista de espera
                    {' · '}
                    {event.participants.filter((p) => p.status === 'cancelled').length} no van
                  </p>
                )}
                <div className="social-meetups-participants-list">
                  {event.participants.map((p) => (
                    <div key={p.userId} className="social-meetups-participant-row">
                      <span className="social-meetups-participant-name">
                        {resolveAvatarUrl(p.photoUrl) && !brokenAvatarKeys.has(`p-${p.userId}`) ? (
                          <img
                            src={resolveAvatarUrl(p.photoUrl)}
                            alt={p.name}
                            className="social-meetups-avatar"
                            onError={() => markAvatarBroken(`p-${p.userId}`)}
                          />
                        ) : (
                          <span className="social-meetups-avatar social-meetups-avatar--fallback">
                            {(p.name || '?').trim().charAt(0).toUpperCase()}
                          </span>
                        )}
                        {p.name}
                        {p.isOrganizer && <span className="social-meetups-organizer-tag"> · Organizador</span>}
                      </span>
                      <span className="social-meetups-participant-actions">
                        <span
                          className={
                            p.status === 'waitlisted'
                              ? 'social-meetups-status-waitlisted'
                              : p.status === 'invited'
                              ? 'social-meetups-status-pending'
                              : p.status === 'cancelled'
                              ? 'social-meetups-status-cancelled'
                              : 'social-meetups-status-confirmed'
                          }
                        >
                          {p.status === 'waitlisted'
                            ? 'En espera'
                            : p.status === 'invited'
                            ? 'Pendiente de confirmar'
                            : p.status === 'cancelled'
                            ? 'No va'
                            : 'Confirmado'}
                        </span>
                        {isOrganizer && !p.isOrganizer && (
                          <select
                            className="social-meetups-status-select"
                            disabled={reinvitingUserId === p.userId}
                            value=""
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value) handleSetParticipantStatus(p, value);
                              e.target.value = '';
                            }}
                          >
                            <option value="">Administrar…</option>
                            {Object.entries(statusActionLabels)
                              .filter(([statusKey]) => statusKey !== p.status)
                              .map(([statusKey, label]) => (
                                <option key={statusKey} value={statusKey}>
                                  {label}
                                </option>
                              ))}
                          </select>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {(hasCoords || event.staticMapUrl) && (
              <section className="social-meetups-section social-meetups-section--full">
                <h2>Mapa de encuentro</h2>
                <div className="social-meetups-map-wrap">
                  {hasCoords ? (
                    <MapContainer
                      center={[eventLat, eventLon]}
                      zoom={14}
                      scrollWheelZoom={false}
                      style={{ width: '100%', height: '100%' }}
                    >
                      <TileLayer
                        attribution="&copy; OpenStreetMap contributors"
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />
                      <Marker position={[eventLat, eventLon]} icon={meetingIcon} />
                    </MapContainer>
                  ) : (
                    <img
                      src={event.staticMapUrl}
                      alt="Mapa del encuentro"
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                  )}
                </div>
              </section>
            )}

            {routePoints.length > 1 && (
              <section className="social-meetups-section social-meetups-section--full">
                <h2>Mapa de ruta (GPX)</h2>
                <div className="social-meetups-map-wrap">
                  <MapContainer
                    center={routeMapCenter}
                    zoom={13}
                    scrollWheelZoom={false}
                    style={{ width: '100%', height: '100%' }}
                  >
                    <TileLayer
                      attribution="&copy; OpenStreetMap contributors"
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <Polyline positions={routePoints} pathOptions={{ color: '#193255', weight: 5 }} />
                  </MapContainer>
                </div>
              </section>
            )}

            {canUseChat && (
              <section className="social-meetups-section social-meetups-section--full">
                <h2>Ubicación en vivo durante el encuentro</h2>

                <label className="social-meetups-checkbox-row">
                  <input
                    type="checkbox"
                    checked={sharingLocation}
                    onChange={(e) => {
                      setLocationError('');
                      setSharingLocation(e.target.checked);
                    }}
                  />
                  Compartir mi ubicación en vivo con el organizador mientras dure el encuentro
                </label>

                <p className="social-meetups-subtext">
                  Es totalmente opcional. Solo se comparte mientras esta página esté abierta y
                  actives la opción — se detiene si cierras la página, sales del encuentro o
                  desactivas la casilla.
                </p>

                {sharingLocation && !locationError && (
                  <p className="social-meetups-status-confirmed">Compartiendo tu ubicación...</p>
                )}
                {locationError && <p className="social-meetups-status-waitlisted">{locationError}</p>}

                {isOrganizer && (
                  <div style={{ marginTop: 14 }}>
                    <p className="social-meetups-subtext">
                      Como organizador, ves aquí a quienes hayan activado "Compartir mi
                      ubicación" en los últimos 20 minutos.
                    </p>

                    {liveLocations.length === 0 ? (
                      <p className="social-meetups-subtext">
                        Nadie está compartiendo su ubicación en este momento.
                      </p>
                    ) : (
                      <div className="social-meetups-map-wrap">
                        <MapContainer
                          center={[liveLocations[0].lat, liveLocations[0].lon]}
                          zoom={13}
                          scrollWheelZoom={false}
                          style={{ width: '100%', height: '100%' }}
                        >
                          <TileLayer
                            attribution="&copy; OpenStreetMap contributors"
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                          />
                          {hasCoords && <Marker position={[eventLat, eventLon]} icon={meetingIcon} />}
                          {liveLocations.map((loc) => (
                            <Marker
                              key={loc.userId}
                              position={[loc.lat, loc.lon]}
                              icon={buildParticipantIcon(loc.name, loc.photoUrl)}
                            />
                          ))}
                        </MapContainer>
                      </div>
                    )}
                  </div>
                )}
              </section>
            )}

            {canUseChat && (
              <section className="social-meetups-section social-meetups-section--full">
                <h2>Chat del grupo</h2>

                <div className="social-meetups-chat-list">
                  {chatMessages.length === 0 && (
                    <p className="social-meetups-subtext" style={{ margin: 0 }}>
                      Todavía no hay mensajes. ¡Sé el primero en escribir!
                    </p>
                  )}

                  {chatMessages.map((m) => (
                    <div
                      key={m.id}
                      className={
                        m.mine
                          ? 'social-meetups-chat-row social-meetups-chat-row--mine'
                          : 'social-meetups-chat-row'
                      }
                    >
                      {!m.mine && (
                        resolveAvatarUrl(m.photoUrl) && !brokenAvatarKeys.has(`m-${m.id}`) ? (
                          <img
                            src={resolveAvatarUrl(m.photoUrl)}
                            alt={m.name}
                            className="social-meetups-avatar"
                            onError={() => markAvatarBroken(`m-${m.id}`)}
                          />
                        ) : (
                          <span className="social-meetups-avatar social-meetups-avatar--fallback">
                            {(m.name || '?').trim().charAt(0).toUpperCase()}
                          </span>
                        )
                      )}
                      <div className={m.mine ? 'social-meetups-chat-bubble social-meetups-chat-bubble--mine' : 'social-meetups-chat-bubble'}>
                        {!m.mine && <div className="social-meetups-chat-author">{m.name}</div>}
                        {m.text}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="social-meetups-chat-input-row">
                  <input
                    value={chatText}
                    onChange={(e) => setChatText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !chatSending) handleSendChat();
                    }}
                    placeholder="Escribe un mensaje para el grupo..."
                    maxLength={1000}
                  />
                  <button
                    type="button"
                    className="social-cta-primary"
                    onClick={handleSendChat}
                    disabled={chatSending || !chatText.trim()}
                  >
                    Enviar
                  </button>
                </div>
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SocialMeetupDetailPage;
